/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, withAssign } from '@reatom/framework'

import { Array, AsyncStateN, Iterable, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { supportTicketUpdatedEvent } from '@root/entities/realtime'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { reatomResourcePaginated } from '@root/shared/lib'

import { SupportTicket } from '../model/support-ticket'

import { fetchSupportTicketsRequestAction } from './support-requests'

import type { SupportTicketId } from '../model/support-ticket-id'
import type { tradeApi } from '@blade/socket-types'
import type { PaginatedData } from '@root/shared/lib'

const reatomSupportTickets = () => {
  const ticketsState = reatomResourcePaginated(
    async (ctx, { limit, offset }) => {
      const result = await ctx.schedule(() =>
        fetchSupportTicketsRequestAction(ctx, { limit, offset }),
      )

      return result.pipe(
        Result.map(
          (it): PaginatedData<SupportTicket, 'offset'> => ({
            data: it.data.map((dto) => new SupportTicket(dto)),
          }),
        ),
      )
    },
    {
      name: 'ticketsState',
      loadOnConnect: true,
      limit: 20,
      strategy: 'offset',
      reconnectStrategy: 'refresh',
    },
  )

  const totalUnreadCount = atom((ctx) => {
    const state = ctx.spy(ticketsState)
    if (!AsyncStateN.isSuccess(state)) {
      return 0
    }
    return pipe(
      state.value.data,
      Iterable.sumBy((it) => ctx.spy(it.unreadCount)),
    )
  })

  const getTicketById = action((ctx, ticketId: SupportTicketId) => {
    const state = ctx.get(ticketsState)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    return state.value.data.find((it) => it.id === ticketId)
  })

  const upsertTicket = action((ctx, ticketDto: tradeApi.SupportTicketData) => {
    const state = ctx.get(ticketsState)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    const existingTicket = state.value.data.find((it) => it.id === ticketDto.id)
    if (existingTicket) {
      existingTicket.updateFromDto(ctx, ticketDto)
      return
    }

    const ticket = new SupportTicket(ticketDto)
    const updatedTickets = pipe(
      state.value.data,
      Array.append(ticket),
      Array.sort(SupportTicket.cmpByUpdatedAt(ctx)),
    )
    ticketsState.setSuccess(ctx, {
      ...state.value,
      data: updatedTickets,
    })
  })

  return ticketsState.pipe(
    withAssign(() => ({
      getTicketById,
      upsertTicket,
      totalUnreadCount,
    })),
  )
}

export const supportTicketsModel = reatomSupportTickets()

export const initializeSupportTickets = reaction(async (ctx) => {
  const isLoggedIn = ctx.spy(isLoggedInAtom)
  if (!isLoggedIn) {
    return
  }

  const { mainWebsocket } = ctx.get(dependenciesAtom)
  mainWebsocket.supportTicketUpdatesSubscribeAction(ctx, {})

  supportTicketsModel.run(ctx)
  supportTicketUpdatedEvent.onCall((ctx, data) => {
    supportTicketsModel.upsertTicket(ctx, data)
  })
}, 'initializeSupportTickets')
